import moment from "moment";
import { StaffHoursType } from "../../redux/types/properties";

export const MIN_OFFSET_MINUTES = 5;
export type ExpiresOnErrors = Record<number, string | null>;
export type ExpiresOnValidationResult = { ok: boolean; msg: string | null };

export function validateSingleExpiresOn(
  val: string | null | undefined
): ExpiresOnValidationResult {
  if (!val) return { ok: true, msg: null };
  const m = moment(val);
  if (!m.isValid()) return { ok: false, msg: "Invalid expiration date." };

  const now = moment();
  const min = now.clone().add(MIN_OFFSET_MINUTES, "minutes");
  if (m.isBefore(now)) return { ok: false, msg: "Expiration cannot be in the past." };
  if (m.isBefore(min)) {
    return {
      ok: false,
      msg: `Choose a time at least ${MIN_OFFSET_MINUTES} minutes from now.`
    };
  }
  return { ok: true, msg: null };
}

export function validateExpiresOnAgainstWindow(
  expiresOnIso: string,
  dayOfWeek: number,
  staffStart: string,
  staffEnd: string
): ExpiresOnValidationResult {
  const expiresOn = moment(expiresOnIso);
  if (!expiresOn.isValid()) {
    return {
      ok: false,
      msg: "Invalid expiration date."
    };
  }

  if (expiresOn.day() !== dayOfWeek)
    return { ok: true, msg: "Expiration isn't in the same day of week" };

  const parsedStart = moment(staffStart, "h:mm A", true);
  const parsedEnd = moment(staffEnd, "h:mm A", true);
  if (!parsedStart.isValid()) {
    return { ok: true, msg: "Invalid staff start" };
  }

  if (!parsedEnd.isValid()) {
    return { ok: true, msg: "Invalid staff start" };
  }

  const start = moment(expiresOn).set({
    hour: parsedStart.hour(),
    minute: parsedStart.minute(),
    second: 0,
    millisecond: 0
  });
  let end = moment(expiresOn).set({
    hour: parsedEnd.hour(),
    minute: parsedEnd.minute(),
    second: 0,
    millisecond: 0
  });

  const crossesMidnight = end.isBefore(start);
  if (crossesMidnight) end = end.add(1, "day");

  const insideWindow = expiresOn.isBetween(start, end, undefined, "[]");

  if (insideWindow) {
    return {
      ok: false,
      msg: `Expiry can’t be during staffed hours (${staffStart} – ${staffEnd}). Choose a time outside the window.`
    };
  }
  return { ok: true, msg: null };
}

export function validateStaffHoursExpiresOn(
  weeklyStaffHours: StaffHoursType[] | null | undefined,
): { valid: boolean; errors: ExpiresOnErrors } {
  const errors: ExpiresOnErrors = {};
  if (!weeklyStaffHours) return { valid: true, errors };

  weeklyStaffHours.forEach((row) => {
    if (!row.isStaffAvailable) {
      errors[row.dayOfWeek] = null;
      return;
    }

    const base = validateSingleExpiresOn(row.expires_on);
    if (!base.ok) {
      errors[row.dayOfWeek] = null;
      return;
    }

    if (row.expires_on && row.staffHoursStart && row.staffHoursEnd) {
      const { ok, msg } = validateExpiresOnAgainstWindow(
        row.expires_on,
        row.dayOfWeek,
        row.staffHoursStart,
        row.staffHoursEnd
      );

      errors[row.dayOfWeek] = ok && !msg ? null : msg;
      return;
    }

    errors[row.dayOfWeek] = null;
  });

  const valid = Object.values(errors).every((v) => !v);
  return { valid, errors };
}
